import { useState, useEffect, useCallback } from "react";
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignUp,
  confirmSignIn,
} from "aws-amplify/auth";

interface AuthUser {
  userId: string;
  email: string;
  role: string;
}

interface UseAuthReturn {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  needsNewPassword: boolean;
  login: (email: string, password: string) => Promise<{ needsNewPassword: boolean }>;
  setNewPassword: (newPassword: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>;
  confirm: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload;

      setUser({
        userId: currentUser.userId,
        email: (claims?.email as string) ?? "",
        role: (claims?.["custom:role"] as string) ?? "user",
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  const login = useCallback(async (email: string, password: string): Promise<{ needsNewPassword: boolean }> => {
    setError(null);
    setNeedsNewPassword(false);
    try {
      let result;
      try {
        result = await signIn({
          username: email,
          password,
          options: { authFlowType: "USER_PASSWORD_AUTH" },
        });
      } catch (err) {
        // If there's a stale session, sign out and retry once
        if (err instanceof Error && err.message.includes("already a signed in user")) {
          await signOut();
          result = await signIn({
            username: email,
            password,
            options: { authFlowType: "USER_PASSWORD_AUTH" },
          });
        } else {
          throw err;
        }
      }

      // Handle force-change-password challenge
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        setNeedsNewPassword(true);
        return { needsNewPassword: true };
      }

      await checkAuth();
      return { needsNewPassword: false };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
      throw err;
    }
  }, []);

  const setNewPassword = useCallback(async (newPassword: string) => {
    setError(null);
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      setNeedsNewPassword(false);
      await checkAuth();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to set new password";
      setError(message);
      throw err;
    }
  }, []);

  const signup = useCallback(
    async (email: string, password: string): Promise<{ needsConfirmation: boolean }> => {
      setError(null);
      try {
        const result = await signUp({
          username: email,
          password,
          options: { userAttributes: { email } },
        });
        return { needsConfirmation: !result.isSignUpComplete };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Signup failed";
        setError(message);
        throw err;
      }
    },
    []
  );

  const confirm = useCallback(async (email: string, code: string) => {
    setError(null);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Confirmation failed";
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut();
    setUser(null);
    // Force full page reload to clear all component state —
    // useAuth is not a context, so other components won't see setUser(null)
    window.location.href = "/login";
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error("Not authenticated");
    return token;
  }, []);

  return { user, loading, error, needsNewPassword, login, setNewPassword, signup, confirm, logout, getToken };
}
